import React from 'react';
import './parts.css';
import { User } from 'lucide-react';

/**
 * WHO YOU APPEAR AS — the consent screen's half of the identity model.
 *
 * It sits AFTER the realm chooser, and that position is the argument: the realm you
 * pick is what proposes a face, so stating one above it would name an identity before
 * the thing that determines it has been chosen. Pick several realms and they may
 * propose different identities, which is the one case only a person can settle; pick
 * realms that name none and your default stands in.
 *
 * The identity is also a line in the ask above, because it is data like any other and
 * an app that never asked for `profile` does not get it. That is what the last state
 * here says out loud, rather than letting a choice look like it reached the app.
 */
/** A face is a photo when it has one, the person glyph when it has none — never
 *  initials: choosing which face to present by its first two letters is choosing blind. */
function Tile({ identity, name }) {
  const [failed, setFailed] = React.useState(false);
  const url = identity?.avatar_url;
  if (url && !failed) {
    return (
      <img className="gp-explain__tile gp-face__tile gp-face__photo" src={url} alt=""
           onError={() => setFailed(true)} />
    );
  }
  return <span className="gp-explain__tile gp-face__tile" title={name || identity?.name}><User size={16} strokeWidth={1.7} aria-hidden="true" /></span>;
}

export default function FaceChoice({
  face, identities = [], owned, onPick, onShowAll, appName = 'This app', username,
  sources = {}, everyone = [], onFollow, follow,
}) {
  // `identities` is what this grant MAY present — the chosen realms' faces plus
  // your default. `owned` is how many you have in total, which is what "every
  // identity" is offering and what its count has to say. `sources` names the
  // realm that proposed each offered face; `everyone` is the owned list, for the
  // pictures on the "every identity" row.
  const total = owned ?? identities.length;
  const [picking, setPicking] = React.useState(false);
  const current = identities.find(i => String(i.id) === String(face?.id)) || null;
  const name = current?.name || null;

  if (!identities.length) {
    return (
      <div className="gp-locked" data-testid="face-none-yet">
        <div>
          <div className="gp-locked__t">You have no identity yet.</div>
          <div className="gp-locked__d">
            {appName} will see your username{username ? <>, <b>{username}</b></> : ''}, until
            you make one in Self → Profile.
          </div>
        </div>
      </div>
    );
  }

  /* WHERE IT CAME FROM, beside the name it qualifies: the realm that proposed
   * it, your own hand, your default — or, on `All`, what the app then sees.
   * That two realms disagree is the one state that asks for a decision. */
  const note = (i) => (i.is_default ? 'your default'
    : (sources[String(i.id)] ? `from ${sources[String(i.id)]}` : null));
  const said = face?.showsAll ? `${appName} sees all ${total} of your identities`
    : face?.contested ? 'Two realms disagree. Pick one.'
    : face?.source === 'realm' ? `from ${face.realmName || 'the realm you picked'}`
    : face?.source === 'chosen' ? 'you chose this'
    : current?.is_default ? 'your default' : null;

  const pick = (fn) => { fn(); setPicking(false); };
  const opt = ({ key, on, tile, label, sub, onClick }) => (
    <button type="button" key={key} onClick={onClick} role="radio" aria-checked={on}
            className={`gp-face__opt${on ? ' is-on' : ''}`}>
      {tile}
      <span className="gp-face__opt-name">{label}</span>
      {sub && <span className="gp-face__opt-note">{sub}</span>}
    </button>
  );

  return (
    <>
      <div className="gp-face" data-testid="face-row">
        {face?.showsAll
          ? <Stack people={everyone.length ? everyone : identities} />
          : <Tile identity={current} name={name} />}
        <div className="gp-face__who">
          <span className="gp-explain__name">
            {face?.showsAll ? 'Every identity' : (name || 'No identity')}
          </span>
          {said && <span className="gp-face__from">{said}</span>}
        </div>
        {onPick && (
          <button type="button" className="gp-face__change" aria-expanded={picking}
                  onClick={() => setPicking((v) => !v)}>
            {picking ? 'Cancel' : 'Change'}
          </button>
        )}
      </div>
      {/* ONE LIST, AT ANY COUNT, AS WIDE AS THE PAGE. Every face this grant may
          present, each with its picture — choosing which face to show by its
          first two letters is choosing blind — and where it came from. Picking
          one closes it. */}
      {picking && (
        <div className="gp-face__list" role="radiogroup" data-testid="face-picker"
             aria-label={`How you appear to ${appName}`}>
          {/* BACK TO THE REALMS. Once an identity is picked here it is this
              relationship's own, and a later change to the realm's identity no
              longer reaches it; this hands the question back. Offered only
              while there is a pick to undo. `follow` names what the realms
              would show. */}
          {onFollow && face?.source === 'chosen' && opt({
            key: 'follow',
            on: false,
            tile: <Tile identity={identities.find(i => String(i.id) === String(follow?.id)) || null} />,
            label: 'As its realms say',
            sub: follow?.note || null,
            onClick: () => pick(() => onFollow()),
          })}
          {identities.map((i) => opt({
            key: i.id,
            on: String(i.id) === String(face?.id) && !face?.showsAll,
            tile: <Tile identity={i} />,
            label: i.name,
            sub: note(i),
            onClick: () => pick(() => onPick?.(String(i.id))),
          }))}
          {/* OFFERED EVERYWHERE, NEVER INFERRED: `all` is only ever chosen here. */}
          {onShowAll && opt({
            key: 'all',
            on: !!face?.showsAll,
            tile: <Stack people={everyone.length ? everyone : identities} />,
            label: 'Every identity',
            sub: `all ${total} of yours`,
            onClick: () => pick(() => onShowAll(!face?.showsAll)),
          })}
        </div>
      )}
    </>
  );
}

/** Every identity, overlapped: the row that stands for all of them wears all of them. */
function Stack({ people = [] }) {
  return (
    <span className="gp-face__stack" aria-hidden="true">
      {people.slice(0, 4).map((i) => <Tile key={i.id} identity={i} />)}
    </span>
  );
}
